import { getDay, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { View } from 'react-big-calendar';
import { PrimeIcons } from 'primereact/api';
import { EXTERNAL_USER_GENDERS, EXTERNAL_USER_STATUSES, USER_ROUTINE_EVENTS } from '../../../types/externalUser';

export const getGenderDisplay = (gender: EXTERNAL_USER_GENDERS | undefined) => {
  if (!gender) return;
  if (gender === EXTERNAL_USER_GENDERS.MALE) return 'Hombre';
  if (gender === EXTERNAL_USER_GENDERS.FEMALE) return 'Mujer';
  return 'Otro';
};

export const getRoutineEventDescription = (eventType: USER_ROUTINE_EVENTS, fieldsUpdated?: string[]) => {
  if (eventType === USER_ROUTINE_EVENTS.NEXT_ROUTINE) return 'Próxima rutina';
  if (eventType === USER_ROUTINE_EVENTS.CURRENT_ROUTINE) return 'Rutina actual';
  if (eventType === USER_ROUTINE_EVENTS.PAST_ROUTINE) return 'Rutina pasada';
  if (eventType === USER_ROUTINE_EVENTS.EXPIRED_ROUTINE) return 'Rutina caducada';
  if (eventType === USER_ROUTINE_EVENTS.CURRENT_ROUTINE_NOT_STARTED) return 'Comienza nueva rutina';
  if (eventType === USER_ROUTINE_EVENTS.UPDATE) {
    if (fieldsUpdated?.includes('exercises') && fieldsUpdated?.includes('duration')) return `Ejercicios y duración actualizados`;
    if (fieldsUpdated?.includes('exercises')) return `Ejercicios actualizados`;
    if (fieldsUpdated?.includes('duration')) return `Duración actualizada`;
    return 'Rutina modificada';
  }
  return '';
};

/** The palette color of a routine event, in the calendar and in the list. */
export const getRoutineEventColor = (eventType: USER_ROUTINE_EVENTS) => {
  if (eventType === USER_ROUTINE_EVENTS.CURRENT_ROUTINE) return 'green';
  if (eventType === USER_ROUTINE_EVENTS.NEXT_ROUTINE) return 'blue';
  if (eventType === USER_ROUTINE_EVENTS.PAST_ROUTINE) return 'teal';
  if (eventType === USER_ROUTINE_EVENTS.EXPIRED_ROUTINE) return 'pink';
  if (eventType === USER_ROUTINE_EVENTS.UPDATE) return 'yellow';
  return 'primary';
};

export const getRoutineEventIcon = (eventType: USER_ROUTINE_EVENTS) => {
  if (eventType === USER_ROUTINE_EVENTS.NEXT_ROUTINE) return PrimeIcons.CALENDAR_PLUS;
  if (eventType === USER_ROUTINE_EVENTS.CURRENT_ROUTINE) return PrimeIcons.CALENDAR;
  if (eventType === USER_ROUTINE_EVENTS.CURRENT_ROUTINE_NOT_STARTED) return PrimeIcons.CALENDAR_PLUS;
  if (eventType === USER_ROUTINE_EVENTS.PAST_ROUTINE) return PrimeIcons.CALENDAR_MINUS;
  if (eventType === USER_ROUTINE_EVENTS.EXPIRED_ROUTINE) return PrimeIcons.CALENDAR_MINUS;
  if (eventType === USER_ROUTINE_EVENTS.UPDATE) return PrimeIcons.COG;
  return '';
};

export const getUserStatusDisplay = (status: EXTERNAL_USER_STATUSES) => {
  if (status === EXTERNAL_USER_STATUSES.ACTIVE) return 'Activo';
  if (status === EXTERNAL_USER_STATUSES.INACTIVE) return 'Inactivo';
  return '';
};

/** The range of dates the reservations calendar asks for, from its date and view. */
export const getReservationsDateRange = (calendarDate: Date, calendarView: View): { start: Date; end: Date } => {
  if (calendarView === 'month') {
    return {
      start: startOfWeek(startOfMonth(calendarDate), { weekStartsOn: 1 }),
      end: endOfWeek(endOfMonth(calendarDate), { weekStartsOn: 1 }),
    };
  } else if (calendarView === 'week' || calendarView === 'work_week') {
    return {
      start: startOfWeek(calendarDate, { weekStartsOn: 1 }),
      end: endOfWeek(calendarDate, { weekStartsOn: 1 }),
    };
  } else {
    // day or agenda view - fetch the whole week to have some buffer
    return {
      start: startOfWeek(calendarDate, { weekStartsOn: 1 }),
      end: endOfWeek(calendarDate, { weekStartsOn: 1 }),
    };
  }
};

/**
 * The payment totals of some expenses, the same way the backend adds them up for its answer: a direct payment counts as
 * direct, every other payment type as bonus. Used when the list shows fewer expenses than the backend answered for.
 */
export const getExpenseTotals = (expenses: { amount: number; paymentType: string }[]) => {
  const totalCashAmount = expenses.filter((e) => e.paymentType === 'cash').reduce((sum, e) => sum + e.amount, 0);
  const totalBonusAmount = expenses.filter((e) => e.paymentType !== 'cash').reduce((sum, e) => sum + e.amount, 0);
  return { totalAmount: totalCashAmount + totalBonusAmount, totalCashAmount, totalBonusAmount };
};

/** The month of `date` padded to whole weeks (the month grid starts on Monday, as the calendar draws it). */
export const getMonthGridRange = (date: Date): { start: Date; end: Date } => {
  return {
    start: startOfWeek(startOfMonth(date), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(date), { weekStartsOn: 1 }),
  };
};

/** The hours the profile calendars show: 07:00 to 22:00 today. */
export const getCalendarTimeBounds = (): { minTime: Date; maxTime: Date } => {
  const minTime = new Date();
  minTime.setHours(7, 0, 0);
  const maxTime = new Date();
  maxTime.setHours(22, 0, 0);
  return { minTime, maxTime };
};

export const dayPropGetter = (date: Date) => {
  const day = getDay(date);
  if (day === 0 || day === 6) {
    return {
      className: 'disabled-day',
      style: { backgroundColor: 'var(--surface-sunken)', color: 'var(--text-subtle)' },
    };
  }
  return {};
};
